import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Copy, Upload, Pencil, Trash2, CheckCircle2, FileClock, Send,
} from "lucide-react";

export function ReportsManager() {
  const [reports, setReports] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // { period, json, existingId }
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/reports");
      setReports(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const openEditor = (content, existingId) => {
    const { id, is_current, created_at, updated_at, ...rest } = content;
    setEditing({ period: content.period || "", json: JSON.stringify(rest, null, 2), existingId });
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reports/duplicate");
      toast.success("Duplicated current edition into a draft");
      await load();
      openEditor(data, data.id);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    toast.info("Parsing document with AI — this can take up to a minute…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/reports/parse", fd);
      toast.success("Parsed! Review the values, set a period, then create the edition.");
      openEditor(data, null);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const editExisting = async (id) => {
    try {
      const { data } = await api.get(`/reports/${id}`);
      openEditor(data, id);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const save = async ({ publish } = {}) => {
    let content;
    try {
      content = JSON.parse(editing.json);
    } catch (e) {
      toast.error(`Invalid JSON: ${e.message}`);
      return;
    }
    content.period = editing.period || content.period || "Untitled";
    setSaving(true);
    try {
      let id = editing.existingId;
      if (id) {
        await api.put(`/admin/reports/${id}`, content);
      } else {
        const { data } = await api.post("/admin/reports", content);
        id = data.id;
      }
      if (publish) await api.post(`/admin/reports/${id}/publish`);
      toast.success(publish ? "Saved & published" : "Saved as draft");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const publish = async (id) => {
    try {
      await api.post(`/admin/reports/${id}/publish`);
      toast.success("Edition published as current");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    const id = toDelete?.id;
    setToDelete(null);
    try {
      await api.delete(`/admin/reports/${id}`);
      toast.success("Edition deleted");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="mt-12">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Monthly Reports</h2>
          <p className="text-slate-500 mt-1">Create, edit and publish briefing editions. The published edition is what viewers see.</p>
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".html,.htm,.pdf,.txt" className="hidden" onChange={onFile} data-testid="report-upload-input" />
          <Button variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="report-upload-button" className="h-10">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> Upload & parse</>}
          </Button>
          <Button disabled={busy} onClick={duplicate} data-testid="report-duplicate-button" className="h-10 bg-primary hover:bg-primary/90">
            <Copy className="w-4 h-4 mr-1.5" /> Duplicate current
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl mt-6 overflow-hidden">
        {reports === null ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : reports.length === 0 ? (
          <div className="p-10 text-center text-slate-400 text-sm">No editions yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="reports-manager-list">
            {reports.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-3 px-5 py-4" data-testid={`report-row-${r.period}`}>
                <FileClock className="w-4 h-4 text-slate-300 shrink-0" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-num text-sm font-semibold text-slate-800">{r.period}</span>
                    {r.is_current && (
                      <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                        <CheckCircle2 className="w-3 h-3" /> Current
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 truncate">{r.title}</div>
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  {!r.is_current && (
                    <button onClick={() => publish(r.id)} data-testid={`report-publish-${r.period}`} className="flex items-center gap-1 text-xs text-primary hover:bg-blue-50 rounded-md px-2.5 py-1.5">
                      <Send className="w-3.5 h-3.5" /> Publish
                    </button>
                  )}
                  <button onClick={() => editExisting(r.id)} data-testid={`report-edit-${r.period}`} className="flex items-center gap-1 text-xs text-slate-600 hover:bg-slate-100 rounded-md px-2.5 py-1.5">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  {!r.is_current && (
                    <button onClick={() => setToDelete(r)} data-testid={`report-delete-${r.period}`} className="text-slate-300 hover:text-destructive p-1.5 rounded-md hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editing?.existingId ? "Edit edition" : "Create new edition"}</DialogTitle>
            <DialogDescription>
              Set the edition period and adjust values. The JSON below is the full report snapshot — edit any numbers or text, then save.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">Edition period (e.g. "Jul 2026")</label>
                <Input
                  data-testid="report-editor-period"
                  value={editing.period}
                  onChange={(e) => setEditing({ ...editing, period: e.target.value })}
                  className="mt-1.5 h-10"
                  placeholder="Jul 2026"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Report content (JSON)</label>
                <Textarea
                  data-testid="report-editor-json"
                  value={editing.json}
                  onChange={(e) => setEditing({ ...editing, json: e.target.value })}
                  className="mt-1.5 font-num text-xs h-72"
                  spellCheck={false}
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => save({ publish: false })} disabled={saving} data-testid="report-save-draft">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save as draft"}
            </Button>
            <Button onClick={() => save({ publish: true })} disabled={saving} data-testid="report-save-publish" className="bg-primary hover:bg-primary/90">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this edition?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-slate-700">{toDelete?.period}</span> will be permanently removed from the archive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="report-confirm-delete" className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
